import React, { useState } from "react";
import SendIcon from "@mui/icons-material/Send";
import CircularProgress from "@mui/material/CircularProgress";

export default function ReportAIAnalysis({
    isOpen,
    isAnalyzing,
    results,
    error,
    onClose,
    onFollowUp,
}) {
    const [followUpQuestion, setFollowUpQuestion] = useState("");

    if (!isOpen) return null;

    const renderResults = () => {
        if (!results) return null;

        // If results is a string, render it directly
        if (typeof results === "string") {
            return formatContent(results);
        }

        // If results is an object, try to render known keys first
        const resultArray = [];

        if (results.success && results.data && results.data.content) {
            // Handle typical API response structure
            return formatContent(results.data.content);
        }

        if (results.content) {
            return formatContent(results.content);
        }

        if (results.summary) {
            resultArray.push(
                <div key="summary" className="tw-mb-4">
                    <h3 className="tw-font-semibold tw-text-slate-900 tw-mb-2">
                        Summary
                    </h3>
                    <div className="tw-text-sm tw-text-slate-700 tw-leading-relaxed">
                        {formatContent(results.summary)}
                    </div>
                </div>,
            );
        }

        if (results.trends) {
            resultArray.push(
                <div key="trends" className="tw-mb-4">
                    <h3 className="tw-font-semibold tw-text-slate-900 tw-mb-2">
                        Trends
                    </h3>
                    <div className="tw-text-sm tw-text-slate-700 tw-leading-relaxed">
                        {formatContent(results.trends)}
                    </div>
                </div>,
            );
        }

        if (results.anomalies) {
            resultArray.push(
                <div key="anomalies" className="tw-mb-4">
                    <h3 className="tw-font-semibold tw-text-slate-900 tw-mb-2">
                        Anomalies
                    </h3>
                    <div className="tw-text-sm tw-text-slate-700 tw-leading-relaxed">
                        {formatContent(results.anomalies)}
                    </div>
                </div>,
            );
        }

        if (results.recommendations) {
            resultArray.push(
                <div key="recommendations" className="tw-mb-4">
                    <h3 className="tw-font-semibold tw-text-slate-900 tw-mb-2">
                        Recommendations
                    </h3>
                    <div className="tw-text-sm tw-text-slate-700 tw-leading-relaxed">
                        {formatContent(results.recommendations)}
                    </div>
                </div>,
            );
        }

        if (results.analysis) {
            resultArray.push(
                <div key="analysis" className="tw-mb-4">
                    <h3 className="tw-font-semibold tw-text-slate-900 tw-mb-2">
                        Analysis
                    </h3>
                    <div className="tw-text-sm tw-text-slate-700 tw-leading-relaxed">
                        {formatContent(results.analysis)}
                    </div>
                </div>,
            );
        }

        // If we found results, render them
        if (resultArray.length > 0) {
            return <>{resultArray}</>;
        }

        // Check for other common keys and render them
        const otherKeys = Object.keys(results).filter(
            (key) => !["success", "data"].includes(key),
        );
        if (otherKeys.length > 0) {
            return (
                <>
                    {otherKeys.map((key) => (
                        <div key={key} className="tw-mb-4">
                            <h3 className="tw-font-semibold tw-text-slate-900 tw-mb-2 tw-capitalize">
                                {key.replace(/_/g, " ")}
                            </h3>
                            <div className="tw-text-sm tw-text-slate-700 tw-leading-relaxed">
                                {formatContent(
                                    typeof results[key] === "string"
                                        ? results[key]
                                        : JSON.stringify(results[key], null, 2),
                                )}
                            </div>
                        </div>
                    ))}
                </>
            );
        }

        // Otherwise, render the entire object as formatted text
        return formatContent(JSON.stringify(results, null, 2));
    };

    const formatContent = (content) => {
        if (!content) return null;

        const text = String(content);

        // Split by newlines and render as paragraphs
        return text.split("\n").map((line, idx) => {
            const trimmed = line.trim();

            // Skip empty lines
            if (!trimmed) {
                return <div key={idx} className="tw-h-2"></div>;
            }

            // Bold lines that look like headers (contain colons at the start)
            if (trimmed.match(/^[A-Z][^.!?]*:/) || trimmed.match(/^[-•]/)) {
                return (
                    <div
                        key={idx}
                        className="tw-font-semibold tw-text-slate-800 tw-mt-2"
                    >
                        {trimmed}
                    </div>
                );
            }

            // Regular paragraph
            return (
                <div key={idx} className="tw-text-slate-700">
                    {trimmed}
                </div>
            );
        });
    };

    const handleSendQuestion = () => {
        if (followUpQuestion.trim() && onFollowUp) {
            onFollowUp(followUpQuestion);
            setFollowUpQuestion("");
        }
    };

    return (
        <div className="ai-panel-container">
            {/* Content */}
            <div
                className="ai-panel-content"
                style={{ maxHeight: 300, overflow: "scroll" }}
            >
                {isAnalyzing ? (
                    <div className="tw-flex tw-flex-col tw-items-center tw-justify-center tw-h-full tw-gap-4">
                        <CircularProgress />
                        <p className="tw-text-sm tw-text-slate-600">
                            Analyzing data with AI...
                        </p>
                    </div>
                ) : error ? (
                    <div className="tw-p-4 tw-bg-red-50 tw-border tw-border-red-200 tw-rounded-lg">
                        <h3 className="tw-font-semibold tw-text-red-900 tw-mb-2">
                            Analysis Error
                        </h3>
                        <p className="tw-text-sm tw-text-red-700">{error}</p>
                    </div>
                ) : results ? (
                    renderResults()
                ) : (
                    <div className="tw-text-center tw-text-slate-500">
                        <p className="tw-text-sm">
                            Click the "Analyze with AI" button to start analysis
                        </p>
                    </div>
                )}
            </div>

            {/* Question Input Footer */}
            {results && !isAnalyzing && !error && (
                <div className="ai-panel-footer">
                    <div className="tw-flex tw-items-end tw-gap-2">
                        <input
                            type="text"
                            value={followUpQuestion}
                            onChange={(e) =>
                                setFollowUpQuestion(e.target.value)
                            }
                            onKeyPress={(e) => {
                                if (e.key === "Enter") {
                                    handleSendQuestion();
                                }
                            }}
                            placeholder="Ask a follow-up question..."
                            className="tw-flex-1 tw-px-3 tw-py-2 tw-border tw-border-slate-300 tw-rounded-md tw-text-sm tw-bg-white focus:tw-outline-none focus:tw-ring-2 focus:tw-ring-purple-500 focus:tw-border-transparent"
                        />
                        <button
                            onClick={handleSendQuestion}
                            disabled={!followUpQuestion.trim() || isAnalyzing}
                            className="tw-p-2 tw-bg-gradient-to-r tw-from-purple-500 tw-to-indigo-600 tw-text-white tw-rounded-md tw-transition-all hover:tw-shadow-md disabled:tw-opacity-50 disabled:tw-cursor-not-allowed"
                            title="Send question"
                        >
                            <SendIcon fontSize="small" />
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
}
